import { useState, useEffect, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import api from './utils/api';
import './App.css';

// Layout Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';

// Page Components
import Home from './components/pages/Home';
import ProductList from './components/pages/ProductList';
import ProductDetail from './components/pages/ProductDetail';
import Cart from './components/pages/Cart';
import Checkout from './components/pages/Checkout';
import PaymentSuccess from './components/pages/PaymentSuccess';
import PaymentCancel from './components/pages/PaymentCancel';
import PaymentGateway from './components/pages/PaymentGateway';
import Register from './components/auth/Register';
import Login from './components/auth/Login';
import PhoneLogin from './components/auth/PhoneLogin';
import Dashboard from './components/dashboard/Dashboard';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminProducts from './components/admin/AdminProducts';
import AdminOrders from './components/admin/AdminOrders';
import AdminAccessCodes from './components/admin/AdminAccessCodes';

// Context
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import AccessCodeContext, { AccessCodeProvider } from './context/AccessCodeContext';

// Access Code Verification
import AccessCodeVerification from './components/AccessCodeVerification';

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin }) => {
  const [auth, setAuth] = useState({
    isAuthenticated: false,
    isAdmin: false,
    loading: true
  });

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setAuth({ isAuthenticated: false, isAdmin: false, loading: false });
        return;
      }
      
      try {
        const res = await api.get('/api/auth/me');
        
        setAuth({
          isAuthenticated: true,
          isAdmin: res.data.role === 'admin',
          loading: false
        });
      } catch {
        localStorage.removeItem('token');
        setAuth({ isAuthenticated: false, isAdmin: false, loading: false });
      }
    };
    
    checkAuth();
  }, []);

  if (auth.loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (requireAdmin && !auth.isAdmin) {
    return <Navigate to="/" />;
  }

  return children;
};

// Wrapper component to handle access code verification
const AccessCodeWrapper = ({ children }) => {
  const { isVerified, loading } = useContext(AccessCodeContext);
  
  console.log('Access code verification status:', { isVerified, loading });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="neumorph p-8 rounded-full">
          <svg className="animate-spin h-8 w-8 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }

  if (!isVerified) {
    return <AccessCodeVerification />;
  }

  return children;
};

function App() {
  // API is already configured in utils/api.js

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID || ''}>
      <AuthProvider>
        <CartProvider>
          <AccessCodeProvider>
            <Router>
              <AccessCodeWrapper>
                <div className="app-container bg-neumorph">
                  <Navbar />
                  <main className="content-container container mx-auto px-4 py-8">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/products" element={<ProductList />} />
                  <Route path="/products/:id" element={<ProductDetail />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/login/phone" element={<PhoneLogin />} />
                  <Route 
                    path="/checkout" 
                    element={
                      <ProtectedRoute>
                        <Checkout />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/payment/gateway" 
                    element={
                      <ProtectedRoute>
                        <PaymentGateway />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/payment/success" 
                    element={
                      <ProtectedRoute>
                        <PaymentSuccess />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/payment/cancel" 
                    element={
                      <ProtectedRoute>
                        <PaymentCancel />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/dashboard" 
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/products" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminProducts />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/products/new" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminProducts showAddForm={true} />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/orders" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminOrders />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/access-codes" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminAccessCodes />
                      </ProtectedRoute>
                    } 
                  />
                </Routes>
                  </main>
                  <Footer />
                </div>
              </AccessCodeWrapper>
            </Router>
          </AccessCodeProvider>
        </CartProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}



export default App;
